import React from 'react';
import { Check, ArrowRight, Mail, Phone } from 'lucide-react';
import { Language } from '../types';

interface ThankYouProps {
  lang: Language;
}

export const ThankYou: React.FC<ThankYouProps> = ({ lang }) => {
  const isRTL = lang === 'ar';

  return (
    <div className="fixed inset-0 z-[70] bg-luxury-paper overflow-y-auto" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="min-h-screen flex items-center justify-center px-6 py-12">
        <div className="max-w-2xl w-full text-center animate-fade-up">
          {/* Success Icon */}
          <div className="mb-8 flex justify-center">
            <div className="w-24 h-24 rounded-full bg-greenline-100 flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-greenline-900 flex items-center justify-center">
                <Check size={32} className="text-white" strokeWidth={2} />
              </div>
            </div>
          </div>

          {/* Heading */}
          <h1 className="font-serif text-4xl md:text-5xl text-luxury-black mb-6">
            {lang === 'ar' ? 'شكراً لتواصلك معنا' : 'Thank You for Reaching Out'}
          </h1>

          {/* Message */}
          <p className="text-lg md:text-xl text-stone-600 font-light mb-12 leading-relaxed">
            {lang === 'ar'
              ? 'تم استلام طلبك بنجاح. سيتواصل فريقنا معك قريباً.'
              : 'Your submission has been received successfully. Our team will be in touch with you shortly.'}
          </p>

          {/* Next Steps */}
          <div className="bg-white border border-stone-200 rounded-lg p-8 mb-12">
            <h2 className="text-sm font-bold uppercase tracking-widest text-greenline-900 mb-6">
              {lang === 'ar' ? 'الخطوات التالية' : 'What Happens Next?'}
            </h2>
            <div className="space-y-4 text-left">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-greenline-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-greenline-900 font-bold text-sm">1</span>
                </div>
                <div>
                  <h3 className="font-medium text-luxury-black mb-1">
                    {lang === 'ar' ? 'مراجعة الطلب' : 'Request Review'}
                  </h3>
                  <p className="text-sm text-stone-600">
                    {lang === 'ar'
                      ? 'سيقوم فريقنا بمراجعة طلبك بعناية.'
                      : 'Our team will carefully review your request.'}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-greenline-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-greenline-900 font-bold text-sm">2</span>
                </div>
                <div>
                  <h3 className="font-medium text-luxury-black mb-1">
                    {lang === 'ar' ? 'التواصل معك' : 'Contact You'}
                  </h3>
                  <p className="text-sm text-stone-600">
                    {lang === 'ar'
                      ? 'سنتواصل معك خلال 24 ساعة عمل.'
                      : 'We will contact you within 24 business hours.'}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-greenline-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-greenline-900 font-bold text-sm">3</span>
                </div>
                <div>
                  <h3 className="font-medium text-luxury-black mb-1">
                    {lang === 'ar' ? 'تقديم الحل' : 'Provide Solution'}
                  </h3>
                  <p className="text-sm text-stone-600">
                    {lang === 'ar'
                      ? 'سنقدم لك عرض سعر مفصل أو نرد على استفسارك.'
                      : 'We will provide you with a detailed quote or answer your inquiry.'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="flex flex-col md:flex-row gap-6 justify-center mb-12">
            <a
              href="tel:+218612345678"
              className="flex items-center gap-3 px-6 py-4 bg-white border border-stone-200 rounded-lg hover:border-greenline-900 hover:bg-stone-50 transition-all"
            >
              <Phone size={20} className="text-greenline-900" />
              <span className="font-serif text-lg text-luxury-black">+218 61 234 5678</span>
            </a>
            <a
              href="mailto:info@greenline-ly.com"
              className="flex items-center gap-3 px-6 py-4 bg-white border border-stone-200 rounded-lg hover:border-greenline-900 hover:bg-stone-50 transition-all"
            >
              <Mail size={20} className="text-greenline-900" />
              <span className="font-serif text-lg text-luxury-black">info@greenline-ly.com</span>
            </a>
          </div>

          {/* Return Home Button */}
          <a
            href="/"
            className="inline-flex items-center gap-3 bg-luxury-black text-white px-10 py-4 text-xs uppercase tracking-[0.2em] hover:bg-greenline-900 transition-colors"
          >
            {lang === 'ar' ? 'العودة للرئيسية' : 'Return to Home'}
            <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
          </a>
        </div>
      </div>
    </div>
  );
};
